// Shared helpers for showing a status pill together with the relevant
// person's name (e.g. "Pending - Amit Kumar"), and the Open/Close pill used
// on Travel Advance Request cards.

const lastAction = (history, action) => {
  if (!Array.isArray(history)) return null;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].action === action) return history[i];
  }
  return null;
};

// Who the record is currently waiting on / was last acted on by, for an
// Advance Request.
export function advanceActorName(r) {
  switch (r.status) {
    case 'PENDING_MANAGER':
      return r.assignedManagerId?.userName || r.requestedBy?.reportingManagerId?.userName || null;
    case 'PENDING_FINANCE':
      return r.assignedFinanceId?.userName || r.requestedBy?.financeId?.userName || null;
    case 'APPROVED':
      return lastAction(r.approvalHistory, 'APPROVED')?.approverId?.userName || null;
    case 'REJECTED':
      return lastAction(r.approvalHistory, 'REJECTED')?.approverId?.userName || null;
    default:
      return null;
  }
}

// Who the record is currently waiting on / was last acted on by, for an
// Expense Claim.
export function claimActorName(r) {
  switch (r.status) {
    case 'SUBMITTED':
      return r.requestedBy?.reportingManagerId?.userName || null;
    case 'MANAGER_APPROVED':
      return r.requestedBy?.financeId?.userName || null;
    case 'FINANCE_APPROVED':
      return r.requestedBy?.financeId?.userName || null;
    case 'SETTLED':
      return lastAction(r.approvalHistory, 'SETTLED')?.approverId?.userName || null;
    case 'REJECTED':
      return lastAction(r.approvalHistory, 'REJECTED')?.approverId?.userName || null;
    default:
      return null;
  }
}

// e.g. withActor('Pending Manager', 'Amit Kumar') -> 'Pending Manager - Amit Kumar'
export function withActor(label, name) {
  return name ? `${label} - ${name}` : label;
}

// Claim payment state: Paid once Finance has settled it, Not Paid once
// Finance-approved but not yet settled. Returns null for all earlier stages
// (Submitted / Manager Approved / Rejected) where "paid" doesn't apply yet.
export function paymentPill(r) {
  if (r.status === 'SETTLED') return { label: 'Paid', cls: 'status-approved', icon: 'bi-check2-all' };
  if (r.status === 'FINANCE_APPROVED') return { label: 'Not Paid', cls: 'status-pending', icon: 'bi-hourglass' };
  return null;
}

// Advance Request "Open / Close" pill: Closed only when the amount actually
// released to the employee exactly matches the estimated total expense on
// file (i.e. the advance fully — and only — covers the estimate, no more, no
// less). Any other case (nothing released yet, released less than the
// estimate, or released more than the estimate) is Open. Only shown when an
// estimate is actually on file — otherwise there's nothing to reconcile
// against.
export function advanceOpenClosePill(r) {
  const est = Number(r.estimatedTotalExpense) || 0;
  if (!est) return null; // no estimate on file — nothing to compare
  const released = Number(r.advanceReleasedAmount) || 0;
  const isComplete = released > 0 && released === est;
  return isComplete
    ? { label: 'Closed', cls: 'status-approved' }
    : { label: 'Open', cls: 'status-hold' };
}
